import React, {useCallback} from 'react';

import {useTranslation} from 'react-i18next';

import {ReturnGoodsReport} from 'types';

import {
    AdditionalInfoContainer,
    InfoDivider,
    InfoField,
    InfoLabel,
    InfoRow,
    InfoValue,
} from '../styled';
import {formatReturnGoodsDate} from '../utils';

type InfoItem = {
    labelKey: string;
    value: string;
};

const InfoPair = ({left, right}: {left: InfoItem; right?: InfoItem}) => {
    const {t} = useTranslation();
    const translate = (key: string) => t(`pages.documents.returnGoods.details.${key}`);

    return (
        <InfoRow>
            <InfoField>
                <InfoLabel>{translate(left.labelKey)}</InfoLabel>
                <InfoValue>{left.value}</InfoValue>
            </InfoField>
            {right && (
                <InfoField>
                    <InfoLabel>{translate(right.labelKey)}</InfoLabel>
                    <InfoValue>{right.value}</InfoValue>
                </InfoField>
            )}
        </InfoRow>
    );
};

export const AdditionalInfo = ({data}: {data: ReturnGoodsReport}) => {
    const returnInfo = data?.reportInfo as ReturnGoodsReport['reportInfo'];
    const {t} = useTranslation();
    const translate = useCallback(
        (dictionary: string, key?: string) => {
            if (!key) return '-';
            return t(`dictionaries.documents.${dictionary}.${key}`);
        },
        [t],
    );

    return (
        <AdditionalInfoContainer>
            <InfoPair
                left={{labelKey: 'returnDocumentId', value: data.reportId ?? '-'}}
                right={{
                    labelKey: 'documentType',
                    value: translate('type', data.reportType),
                }}
            />
            <InfoPair
                left={{
                    labelKey: 'status',
                    value: translate('status', data?.status),
                }}
                right={{
                    labelKey: 'source',
                    value: translate('returnGoods.source', returnInfo.source),
                }}
            />
            <InfoPair
                left={{labelKey: 'organization', value: returnInfo.organization}}
                right={{labelKey: 'createdBy', value: returnInfo.createdBy}}
            />
            <InfoDivider>
                <InfoPair
                    left={{
                        labelKey: 'createdAt',
                        value: formatReturnGoodsDate(data?.createdAt),
                    }}
                    right={{
                        labelKey: 'processedAt',
                        value: formatReturnGoodsDate(returnInfo.processedAt),
                    }}
                />
            </InfoDivider>
            <InfoPair
                left={{
                    labelKey: 'previousCodeStatus',
                    value: translate('type', returnInfo.previousCodeStatus),
                }}
                right={{
                    labelKey: 'newCodeStatus',
                    value: translate('type', returnInfo.newCodeStatus),
                }}
            />
            <InfoPair
                left={{labelKey: 'cashRegisterId', value: returnInfo.cashRegisterId ?? '-'}}
                right={{labelKey: 'pointOfSaleId', value: String(returnInfo.pointOfSaleId ?? '-')}}
            />
            <InfoPair
                left={{
                    labelKey: 'typeOfReturn',
                    value: translate('returnType', returnInfo.returnType),
                }}
            />
        </AdditionalInfoContainer>
    );
};
